import {
  ColumnDef,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
} from '@tanstack/react-table';

import { SimpleTable } from './SimpleTable';
import {
  RowWrapper,
  TablePaginationConfig,
  TableRowHeightVariant,
} from './types';
import { VirtuosoTable } from './VirtuosoTable';

// Unified Table component interface - handles both simple and virtualized rendering
interface TableProps<T extends Record<string, any> | null> {
  actionColId?: string;
  data: T[];
  columns: ColumnDef<T>[];
  variant?: TableRowHeightVariant;
  bordered?: boolean;
  useVirtuoso?: boolean;
  alwaysShowCheckbox?: boolean;
  getGroupId?: (data: T) => string;
  getItemId?: (data: T) => string;
  getRowId?: (data: T, index: number) => string; // Custom row ID generator
  isSelectionDisabled?: (data: T) => string | undefined;
  onRowSelectionChange?: (updater: Updater<RowSelectionState>) => void;
  onExpandedChange?: (updater: any) => void; // For expandable rows
  onPaginationChange?: (updater: Updater<PaginationState>) => void;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  onFilteredRowsChange?: (rows: Row<T | null>[] | undefined) => void;
  enableRowSelection?: boolean;
  enableMultiRowSelection?: boolean;
  enableSubRowSelection?: boolean;
  onRowShiftSelect?: (
    tableRows: Row<T | null>[] | undefined,
    row: Row<T | null>,
    index: number
  ) => void;
  pagination?: TablePaginationConfig;
  className?: string;
  onRowClick?: (row: T, index: number) => void;
  summaryData?: T | null;
  style?: React.CSSProperties;
  state?: Partial<TableState>;
  sortingFns?: Record<string, any>;
  meta?: Record<string, any>;
  footerCta?: React.ReactNode;
  rowWrapper?: RowWrapper<T>;
  getSubRows?: (row: T) => T[]; // For expandable rows
  getRowCanExpand?: (row: { original: T }) => boolean; // For expandable rows
  manualPagination?: boolean;
  rowCount?: number;
}

// Unified Table component - automatically uses Virtuoso for large datasets
export const Table = <T extends Record<string, any>>(props: TableProps<T>) => {
  const { useVirtuoso, data } = props;

  if (useVirtuoso) {
    return <VirtuosoTable {...props} />;
  }

  return <SimpleTable {...props} />;
};
